import { useCallback, useEffect, useRef, useState } from 'react';
import { useAxiosSwagger } from './useAxiosSwagger';
import { describeError } from '../api/requeue';
import { DailyReport, getDailyReport } from '../api/report';

export interface DailyReportMonitor {
    report: DailyReport | null;
    date: Date;
    loading: boolean;
    error: string | null;
    load: (date: Date) => void;
    reload: () => void;
}

const startOfToday = (): Date => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return today;
};

/**
 * Loads the daily report of one environment for one day; yesterday until asked otherwise,
 * because that is the day there is something to say about every morning.
 */
export const useDailyReport = (
    customer: string | undefined,
    environment: string | undefined
): DailyReportMonitor => {
    const axios = useAxiosSwagger();
    const [report, setReport] = useState<DailyReport | null>(null);
    const [date, setDate] = useState<Date>(() => {
        const yesterday = startOfToday();
        yesterday.setDate(yesterday.getDate() - 1);

        return yesterday;
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const controller = useRef<AbortController | null>(null);

    const load = useCallback(
        (next: Date) => {
            setDate(next);

            if (!axios || !customer || !environment) {
                return;
            }

            controller.current?.abort();
            const current = new AbortController();
            controller.current = current;

            setReport(null);
            setLoading(true);
            setError(null);

            getDailyReport(axios, customer, environment, next, current.signal)
                .then((result) => {
                    if (controller.current === current) {
                        setReport(result);
                    }
                })
                .catch((ex) => {
                    if (
                        controller.current === current &&
                        !current.signal.aborted
                    ) {
                        setError(describeError(ex));
                    }
                })
                .finally(() => {
                    if (controller.current === current) {
                        controller.current = null;
                        setLoading(false);
                    }
                });
        },
        [axios, customer, environment]
    );

    const reload = useCallback(() => load(date), [load, date]);

    useEffect(() => {
        setReport(null);
        setError(null);

        return () => controller.current?.abort();
    }, [customer, environment]);

    return { report, date, loading, error, load, reload };
};
